/**
 * HTTP client.
 *
 * Design decisions worth knowing:
 *
 * - The **access token lives in memory only**. It is never written to `localStorage`, so an XSS
 *   cannot read a long-lived credential out of storage. The refresh token is an HttpOnly cookie
 *   the JavaScript never touches.
 * - A 401 triggers **one** refresh attempt, and concurrent 401s share that single attempt rather
 *   than each firing their own.
 * - The backend's error envelope is unwrapped into a typed `ApiError`, so callers handle one shape.
 */
import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

export interface ApiErrorBody {
  error: {
    type: string;
    detail: string;
    fields?: Record<string, string[] | string>;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly type: string;
  readonly fields: Record<string, string>;

  constructor(status: number, type: string, detail: string, fields?: Record<string, string[] | string>) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.type = type;
    this.fields = Object.fromEntries(
      Object.entries(fields ?? {}).map(([key, value]) => [key, Array.isArray(value) ? String(value[0] ?? '') : String(value)]),
    );
  }

  /** Message for the field, or undefined when the error was not field-specific. */
  fieldError(name: string): string | undefined {
    return this.fields[name];
  }

  get isValidation(): boolean {
    return this.status === 400;
  }

  get isUnauthorised(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;
const unauthorisedListeners = new Set<() => void>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onUnauthorised(listener: () => void): () => void {
  unauthorisedListeners.add(listener);
  return () => unauthorisedListeners.delete(listener);
}

function notifyUnauthorised(): void {
  unauthorisedListeners.forEach((listener) => listener());
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export const http: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { Accept: 'application/json' },
  timeout: 30_000,
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  // Django requires the CSRF token on unsafe session-authenticated requests.
  if (config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase())) {
    const csrf = readCookie('csrftoken');
    if (csrf) config.headers['X-CSRFToken'] = csrf;
  }
  return config;
});

async function refreshAccessToken(): Promise<string | null> {
  try {
    const response = await axios.post<{ access: string }>(`${API_BASE}/auth/refresh/`, {}, { withCredentials: true });
    accessToken = response.data.access;
    return accessToken;
  } catch {
    accessToken = null;
    return null;
  }
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const { response, config } = error;

    if (!response) {
      throw new ApiError(0, 'network_error', 'We could not reach the server. Check your connection and try again.');
    }

    const retriable = config && !(config as { _retried?: boolean })._retried;
    const isAuthEndpoint = config?.url?.includes('/auth/login') || config?.url?.includes('/auth/refresh');

    if (response.status === 401 && retriable && !isAuthEndpoint) {
      (config as { _retried?: boolean })._retried = true;
      // Share a single refresh across concurrent failures instead of stampeding the endpoint.
      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
      const token = await refreshPromise;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        return http.request(config);
      }
      notifyUnauthorised();
    }

    const body = response.data;
    if (body && typeof body === 'object' && 'error' in body && body.error) {
      throw new ApiError(response.status, body.error.type, body.error.detail, body.error.fields);
    }
    throw new ApiError(response.status, 'error', `Request failed with status ${response.status}.`);
  },
);

/* ------------------------------------------------------------------ typed helpers */

export interface Paginated<T> {
  count: number;
  page: number;
  pages: number;
  page_size: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export type QueryParams = Record<string, string | number | boolean | undefined | null | (string | number)[]>;

function cleanParams(params?: QueryParams): Record<string, string | number | boolean> | undefined {
  if (!params) return undefined;
  const entries = Object.entries(params).flatMap(([key, value]) => {
    if (value === undefined || value === null || value === '') return [];
    if (Array.isArray(value)) return value.length ? [[key, value.join(',')] as const] : [];
    return [[key, value] as const];
  });
  return entries.length ? Object.fromEntries(entries) : undefined;
}

export async function apiGet<T>(path: string, params?: QueryParams): Promise<T> {
  const response = await http.get<T>(path, { params: cleanParams(params) });
  return response.data;
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const response = await http.post<T>(path, body);
  return response.data;
}

export async function apiPostForm<T>(path: string, form: FormData): Promise<T> {
  const response = await http.post<T>(path, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return response.data;
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const response = await http.patch<T>(path, body);
  return response.data;
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const response = await http.put<T>(path, body);
  return response.data;
}

export async function apiDelete(path: string): Promise<void> {
  await http.delete(path);
}
